import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Input, Toast } from 'components/atoms';
import api from '@/services/api';
import HomeworkScore, { GradingResultSummary } from '@/components/organisms/Card/Homework/HomeworkScore';

interface GradingResult {
  totalItems: number;
  correctItems: number;
  incorrectItems: number;
  score: number;
  completedAt: string;
}

interface UnitActivity {
  id: number;
  title: string;
  activityType: string;
  items?: any[];
}

interface TextbookUnit {
  unitId: number;
  unitTitle: string;
  unitOrderIndex: number;
  status: string;
  activities: UnitActivity[];
  responseJson?: string;
}

interface Textbook {
  textbookId: number;
  textbookTitle?: string; // optional for compatibility
  textbookName?: string;  // service returns textbookName
  units: TextbookUnit[];
}

interface ReportData {
  studentName: string;
  scheduleTitle: string;
  scheduleId: number;
  startDate?: string;
  endDate?: string;
  gradingResult?: GradingResult;
  textbooks?: Textbook[];
  status?: string;
  academyName?: string;
}

export default function PublicReportPage() {
  const { token } = useParams<{ token: string }>();
  const [step, setStep] = useState<'loading' | 'password' | 'report' | 'error'>('loading');
  const [password, setPassword] = useState('');
  const [studentName, setStudentName] = useState('');
  const [scheduleTitle, setScheduleTitle] = useState('');
  const [academyName, setAcademyName] = useState('알파카 잉글리시');
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [toast, setToast] = useState<{ message: string; status: 'success' | 'error' | 'info' } | null>(null);
  const [loading, setLoading] = useState(false);
  const [gradingResult, setGradingResult] = useState<GradingResultSummary | null>(null);

  const checkToken = useCallback(async () => {
    if (!token) {
      setStep('error');
      return;
    }

    try {
      const response = await api.get(`/public/report/${token}`);
      
      if (response.data.success) {
        setStudentName(response.data.data.studentName);
        setScheduleTitle(response.data.data.scheduleTitle);
        if (response.data.data.academyName) {
          setAcademyName(response.data.data.academyName);
        }
        setStep('password');
      } else {
        setToast({ message: response.data.message || '유효하지 않은 링크입니다.', status: 'error' });
        setStep('error');
      }
    } catch (error: any) {
      console.error('토큰 확인 실패:', error);
      setToast({ message: '링크를 확인하는 중 오류가 발생했습니다.', status: 'error' });
      setStep('error');
    }
  }, [token]);

  useEffect(() => {
    checkToken();
  }, [checkToken]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password || password.trim().length !== 6) {
      setToast({ message: '6자리 패스워드를 입력해주세요.', status: 'error' });
      return;
    }

    setLoading(true);

    try {
      const response = await api.post(`/public/report/${token}/verify`, {
        password: password.trim()
      });

      if (response.data.success) {
        setReportData(response.data.data);
        if (response.data.data.academyName) {
          setAcademyName(response.data.data.academyName);
        }
        setGradingResult(null);
        setStep('report');
        setToast({ message: '성적표를 불러왔습니다.', status: 'success' });
      } else {
        setToast({ message: response.data.message || '패스워드가 일치하지 않습니다.', status: 'error' });
      }
    } catch (error: any) {
      console.error('인증 실패:', error);
      setToast({ message: '인증 중 오류가 발생했습니다.', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (step === 'loading') {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full border-b-2 border-orange-500 animate-spin"></div>
          <p className="text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="p-8 w-full max-w-md text-center bg-white rounded-lg shadow-lg">
          <div className="mb-4 text-6xl">❌</div>
          <h1 className="mb-2 text-2xl font-bold text-gray-800">링크 오류</h1>
          <p className="mb-6 text-gray-600">유효하지 않거나 만료된 링크입니다.</p>
          <div className='flex justify-center'>
            <Button onClick={() => window.location.href = '/'}>
              홈으로 이동
            </Button>
          </div>
        </div>
        {toast && <Toast message={toast.message} status={toast.status} isVisible={!!toast} />}
      </div>
    );
  }

  if (step === 'password') {
    return (
      <div className="flex justify-center items-center p-4 min-h-screen bg-gradient-to-br from-orange-50 to-orange-100">
        <div className="p-8 w-full max-w-md bg-white rounded-2xl shadow-2xl">
          <div className="mb-8 text-center">
            <div className="mb-4 text-5xl">🎓</div>
            <h1 className="mb-2 text-3xl font-bold text-gray-800">성적표 조회</h1>
            <p className="text-gray-600">{academyName}</p>
          </div>

          <div className="p-4 mb-6 bg-orange-50 rounded-lg">
            <p className="mb-1 text-sm text-gray-600">학생 이름</p>
            <p className="text-lg font-bold text-gray-800">{studentName}</p>
            <p className="mt-2 mb-1 text-sm text-gray-600">숙제명</p>
            <p className="text-lg font-bold text-gray-800">{scheduleTitle}</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label className="block mb-2 text-sm font-bold text-gray-700">
                🔐 패스워드 (6자리)
              </label>
              <Input
                type="text"
                placeholder="이메일에서 확인한 6자리 숫자"
                value={password}
                onChange={(e) => setPassword(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                maxLength={6}
                className="text-2xl tracking-widest text-center"
                autoFocus
              />
              <p className="mt-2 text-xs text-gray-500">
                ※ 학부모님께 발송된 이메일에서 패스워드를 확인하세요.
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading || password.length !== 6}
              className="w-full"
            >
              {loading ? '확인 중...' : '성적표 보기'}
            </Button>
          </form>
        </div>
        {toast && <Toast message={toast.message} status={toast.status} isVisible={!!toast} />}
      </div>
    );
  }

  // step === 'report'

  return (
    <div className="px-4 py-8 min-h-screen bg-gray-50">
      <div className="mx-auto max-w-6xl">
        <div className="p-8 mb-8 text-center bg-white rounded-2xl shadow-lg">
          <h1 className="mb-2 text-3xl font-bold text-orange-500">성적표</h1>
          <p className="text-gray-500">{academyName}</p>
        </div>

        <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
          <section className="order-2 w-full lg:flex-1 lg:order-1 lg:min-w-[620px]">
            {reportData && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
                <HomeworkScore
                  type='score'
                  data={reportData as any}
                  platform='public'
                  suppressVideoQuizUi
                  excludeVideoQuizFromSummary
                  onGradingResultChange={setGradingResult}
                />
              </div>
            )}
          </section>

          <section className="order-1 lg:order-2 lg:max-w-[420px] lg:sticky lg:top-8">
            <div className="p-6 space-y-6 bg-white rounded-2xl shadow-lg">
              <div>
                <p className="text-sm text-gray-500">학생 이름</p>
                <p className="text-xl font-bold text-gray-900">{reportData?.studentName || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">숙제명</p>
                <p className="text-lg font-semibold text-gray-900">{reportData?.scheduleTitle || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">채점 완료일</p>
                <p className="text-base font-semibold text-gray-900">
                  {reportData?.gradingResult?.completedAt ? formatDate(reportData.gradingResult.completedAt) : '-'}
                </p>
              </div>
              <div className="pt-4 border-t border-gray-100">
                <p className="mb-3 text-sm font-semibold text-gray-600">채점 결과</p>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="py-3 bg-blue-50 rounded-xl">
                    <p className="mb-1 text-xs text-blue-600">총 문항 수</p>
                    <p className="text-2xl font-bold text-blue-700">{gradingResult ? gradingResult.totalItems : '–'}</p>
                  </div>
                  <div className="py-3 bg-green-50 rounded-xl">
                    <p className="mb-1 text-xs text-green-600">정답 수</p>
                    <p className="text-2xl font-bold text-green-700">{gradingResult ? gradingResult.correct : '–'}</p>
                  </div>
                  <div className="py-3 bg-red-50 rounded-xl">
                    <p className="mb-1 text-xs text-red-600">오답 수</p>
                    <p className="text-2xl font-bold text-red-700">{gradingResult ? gradingResult.incorrect : '–'}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="mt-12 text-sm text-center text-gray-500">
          <p>{academyName}</p>
          <p className="mt-1">본 성적표는 {formatDate(new Date().toISOString())}에 조회되었습니다.</p>
        </div>
      </div>
      {toast && <Toast message={toast.message} status={toast.status} isVisible={!!toast} />}
    </div>
  );
}

